import {
  memo,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type TransitionEvent,
} from 'react';
import { flushSync } from 'react-dom';
import { prefersReducedMotion } from '@/components/layout/shell/entrance';
import { cn } from '@/components/ui/cn';
import { queryVariants } from '@/lib/searchText';
import {
  FAN_HALF,
  FAN_LIFT,
  FAN_LIGHT_OFF,
  FAN_LIGHT_ON,
  FAN_PARTIAL_SLACK,
  FAN_RING_R,
  fanAngles,
  fanStep,
  fanTargets,
  hitTest,
  petalContour,
  seamCount,
  seamWidth,
  sectorPath,
} from './fanGeometry';
import { PETAL_D } from './geometry';
import {
  EASE_FOLD,
  EASE_OUT_EXPO,
  EASE_SETTLE,
  EASE_STANDARD,
  FAN_ARRIVE_DELAY_MS,
  FAN_ARRIVE_MS,
  FAN_COMPRESS_MS,
  FAN_FOLD_FADE_MS,
  FAN_FOLD_MS,
  FAN_FOLD_STEP_MS,
  FAN_LIGHT_OFF_MS,
  FAN_LIGHT_ON_MS,
  FAN_PART_CLOSE_MS,
  FAN_PART_OPEN_MS,
  FAN_SPREAD_MS,
  arrivalTiming,
  fanOpenTiming,
  type FanTiming,
} from './schedule';
import { useFanMotion, type FanMotion, type FanTween } from './useFanMotion';

/*
  ServiceFan — веер сервисов («Мои сервисы», spec-4, R43, R41): по пластине на каждый подключённый сервис,
  в порядке подключения, с 6 часов по часовой стрелке. Светлая пластина — сервис включён, контур —
  выключен, красная целиком — сбой. Пластина — ровно PETAL_D знака: твёрдая, одного размера при любом N;
  до шести — кадры раскрытия знака, с семи — шаг 360/N, следующая поверх предыдущей.

  Здесь только svg, попадание и клавиатура. Кто активен и кто закреплён, решает сцена (ServiceFanStage):
  веер сообщает «под указателем вот эта» (onActive … 'pointer'), шаги с клавиатуры, нажатия (onPin).

  Слои пластины: ночной шов (с семи), основа — ночь с контуром, свет — та же пластина, обрезанная
  кругом от заклёпки (включён — круг до кончика, выключен — до внутреннего конца, видна основа; при сбое
  свет красный). У шва на 6 часах копии первых пластин поверх хвостовых — черепица идёт по кругу без
  начала и конца.

  Движутся только поворот вокруг заклёпки, отрыв активной от неё, прозрачность и свет (useFanMotion, без
  React). Раскрытие — раз за сеанс; приход, уход, свет, раздвигание и отрыв — по расписанию schedule.ts.
  В покое веер неподвижен.
*/

export interface FanPlate {
  id: string;
  label: string;
  /** Сервис включён: пластина светлая; выключен — контуром. */
  on: boolean;
  /** Причина сбоя (только у включённого): пластина красная целиком. */
  alert?: string;
}

interface ServiceFanProps {
  /** Пластины в порядке подключения. */
  plates: readonly FanPlate[];
  /** Подпись для читалок: «Мои сервисы: 12, включено 9». */
  label: string;
  activeId?: string | null;
  pinnedId?: string | null;
  /** Под указателем другая пластина (сырое попадание, без таймеров) или шаг с клавиатуры. */
  onActive?: (id: string | null, via: 'pointer' | 'keyboard') => void;
  onPin?: (id: string | null) => void;
  /** Enter / Пробел на пластине: сцена переводит фокус на главное действие карточки. */
  onEnter?: (id: string) => void;
  /** false — картинка (role="img"): без попадания и клавиш, для галереи и превью. */
  interactive?: boolean;
  /** Раскрытие при первом показе: раз за сеанс, никогда или каждый раз. */
  bloom?: 'session' | 'never' | 'always';
  /** Пришли, пока страницы не было на экране: выходят через 300 мс после показа. */
  arriving?: readonly string[];
  /**
   * Состав показан на экране (раскрытием, приходом или сразу на месте): страница помечает его увиденным.
   * Пока веер в скрытой вкладке, пришедшие сюда не попадают — их приход ждёт показа.
   */
  onSeen?: (ids: readonly string[]) => void;
  /** Число сервисов в кольце. */
  count?: boolean;
  /** Галерея: секторы попадания полупрозрачными полосами. */
  debugSectors?: boolean;
  /** id колонки карточки (aria-controls). */
  controls?: string;
  className?: string;
}

/** Больше стольких за раз — уже не событие: без прихода и ухода, сразу на месте. */
const FAN_BATCH_MAX = 6;
/** Приход, случившийся без страницы, начинается после показа. */
const ARRIVE_ON_SHOW_MS = 300;
/** Свет загорается в пришедшей пластине за столько до посадки. */
const LIGHT_BEFORE_LAND_MS = 200;
/** Буфер набора букв для перехода к сервису. */
const TYPEAHEAD_MS = 600;
/** Флаг «раскрытие уже было в этом сеансе». */
const BLOOMED_KEY = 'kaname.fan.bloomed';
/** Обрезка копий у шва шире контура на кромку пластины и сглаживание, px. */
const SEAM_CLIP_PX = 2;

function readBloomed(): boolean {
  try {
    return window.sessionStorage.getItem(BLOOMED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeBloomed() {
  try {
    window.sessionStorage.setItem(BLOOMED_KEY, '1');
  } catch {
    // Хранилище недоступно — раскрытие просто повторится при следующем показе.
  }
}

const fold = (s: string) => s.toLowerCase().replaceAll('ё', 'е');
const safeId = (id: string) => id.replace(/[^a-zA-Z0-9_-]/g, '_');

interface Leaving {
  plate: FanPlate;
  /** Кто стоял перед ней (ближний — первым): по ним уходящая держит своё место в стопке. */
  before: string[];
}

interface Shown {
  plate: FanPlate;
  leaving: boolean;
}

/** Порядок на экране: живые пластины плюс уходящие на своих прежних местах. */
function mergeShown(plates: readonly FanPlate[], leaving: readonly Leaving[]): Shown[] {
  const out: Shown[] = plates.map((plate) => ({ plate, leaving: false }));
  for (const l of leaving) {
    let at = 0;
    for (const id of l.before) {
      const i = out.findIndex((s) => s.plate.id === id);
      if (i >= 0) {
        at = i + 1;
        break;
      }
    }
    out.splice(at, 0, { plate: l.plate, leaving: true });
  }
  return out;
}

/** Голос пластины: «Slack — включён, сбой: webhook не отвечает, 2 из 12». */
function optionLabel(p: FanPlate, k: number, n: number): string {
  const alert = p.on && p.alert ? `, сбой: ${p.alert.charAt(0).toLowerCase()}${p.alert.slice(1)}` : '';
  return `${p.label} — ${p.on ? 'включён' : 'выключен'}${alert}, ${k + 1} из ${n}`;
}

const lightOf = (p: FanPlate) => (p.on ? FAN_LIGHT_ON : FAN_LIGHT_OFF);
/** Активная отрывается от заклёпки, остальные стоят у неё. */
const liftOf = (k: number, activeIdx: number) => (k >= 0 && k === activeIdx ? FAN_LIFT : 0);

/** Раздвигание и отрыв: раскрыть быстро, вернуть медленнее. */
const partTween = (open: boolean): FanTween =>
  open
    ? { duration: FAN_PART_OPEN_MS, ease: EASE_OUT_EXPO }
    : { duration: FAN_PART_CLOSE_MS, ease: EASE_STANDARD };

/* ---------- Движение: что делает каждая пластина при смене состава ---------- */

interface Change {
  motion: FanMotion;
  prev: readonly string[];
  next: readonly FanPlate[];
  activeIdx: number;
}

/** Сразу на место, без движения. */
function place({ motion, next, activeIdx }: Omit<Change, 'prev'>) {
  const target = fanTargets(next.length, activeIdx);
  next.forEach((p, k) =>
    motion.jump(p.id, { angle: target[k], opacity: 1, light: lightOf(p), lift: liftOf(k, activeIdx) }),
  );
}

/** Раскрытие (§7.2). Возвращает, через сколько мс сядет последняя пластина. */
function bloomFan({ motion, next, activeIdx }: Omit<Change, 'prev'>): number {
  const n = next.length;
  const target = fanTargets(n, activeIdx);
  let end = 0;
  next.forEach((p, k) => {
    const t = fanOpenTiming(n, k);
    // До шести каждая выходит из-за предыдущей; с семи все сложены на 6 часах.
    motion.jump(p.id, { angle: n <= 6 ? (k - 1) * 60 : 0, opacity: 0, light: lightOf(p), lift: 0 });
    motion.to(
      p.id,
      { angle: target[k], lift: liftOf(k, activeIdx) },
      { duration: t.duration, delay: t.delay, ease: t.ease },
    );
    motion.to(p.id, { opacity: 1 }, { duration: t.fade, delay: t.delay, ease: 'ease-out' });
    end = Math.max(end, t.delay + t.duration);
  });
  return end;
}

/**
 * Смена состава (§7.3–7.4). Возвращает, сколько длится приход (наведение в это время молчит) и когда
 * можно убрать уходящие пластины.
 */
function changeFan({ motion, prev, next, activeIdx }: Change): { busy: number; done: number } {
  const ids = next.map((p) => p.id);
  const prevSet = new Set(prev);
  const nextSet = new Set(ids);
  const added = ids.filter((id) => !prevSet.has(id));
  const removed = prev.filter((id) => !nextSet.has(id));
  const n = next.length;
  const target = fanTargets(n, activeIdx);

  // Пришло и ушло разом или слишком много за раз — смена сценария, а не событие.
  if (
    (added.length > 0 && removed.length > 0) ||
    added.length > FAN_BATCH_MAX ||
    removed.length > FAN_BATCH_MAX
  ) {
    place({ motion, next, activeIdx });
    return { busy: 0, done: 0 };
  }

  if (removed.length > 0) {
    // Уход: тише и короче прихода. Остальные расходятся к новому шагу.
    next.forEach((p, k) =>
      motion.to(p.id, { angle: target[k] }, { duration: FAN_SPREAD_MS, ease: EASE_STANDARD }),
    );
    const nOld = prev.length;
    const stepOld = fanStep(nOld);
    // Пачкой — от крайней.
    const order = removed.map((id) => prev.indexOf(id)).sort((a, b) => b - a);
    let done = 0;
    order.forEach((i, rank) => {
      const id = prev[i];
      const delay = rank * FAN_FOLD_STEP_MS;
      let angle: number;
      if (nOld <= 6) {
        // Складывается за предыдущую, как лепесток знака.
        angle = (i - 1) * 60;
      } else {
        // Прячется под следующую (последняя — под пластину 0): туда, где следующая встанет.
        const after = prev.slice(i + 1).find((x) => nextSet.has(x));
        const j = after ? ids.indexOf(after) : -1;
        angle =
          j >= 0 ? target[j] : n > 0 ? 360 + target[0] : (motion.value(id)?.angle ?? i * stepOld) + stepOld;
      }
      motion.to(id, { angle }, { duration: FAN_FOLD_MS, delay, ease: EASE_FOLD });
      motion.to(id, { opacity: 0 }, { duration: FAN_FOLD_FADE_MS, delay, ease: 'ease-in' });
      done = Math.max(done, delay + FAN_FOLD_MS);
    });
    return { busy: 0, done: Math.max(done, FAN_SPREAD_MS) };
  }

  if (added.length === 0) return { busy: 0, done: 0 };

  const addedSet = new Set(added);
  const base = fanAngles(n);
  const step = fanStep(n);
  let busy = 0;
  /** Пластина приходит контуром; включённая загорается за 200 мс до посадки. */
  const arrive = (p: FanPlate, start: number, k: number, t: FanTiming, fadeDelay = t.delay) => {
    const now = motion.value(p.id);
    // Вернулась, не успев уйти, — подхватывается с места; иначе выходит из своей начальной позы.
    if (!now || now.opacity < 0.01) motion.jump(p.id, { angle: start, opacity: 0, light: FAN_LIGHT_OFF });
    motion.to(p.id, { angle: target[k] }, { duration: t.duration, delay: t.delay, ease: t.ease });
    motion.to(p.id, { opacity: 1 }, { duration: t.fade, delay: fadeDelay, ease: 'ease-out' });
    if (p.on) {
      motion.to(
        p.id,
        { light: FAN_LIGHT_ON },
        {
          duration: FAN_LIGHT_ON_MS,
          delay: Math.max(t.delay, t.delay + t.duration - LIGHT_BEFORE_LAND_MS),
          ease: EASE_SETTLE,
        },
      );
    } else motion.to(p.id, { light: FAN_LIGHT_OFF }, { duration: FAN_LIGHT_OFF_MS, ease: EASE_FOLD });
    busy = Math.max(busy, t.delay + t.duration);
  };

  if (n <= 6) {
    // Кадр знака: новая неспешно выходит из-за предыдущей (arrivalTiming, spec-1).
    const from = Math.min(...added.map((id) => ids.indexOf(id)));
    next.forEach((p, k) => {
      if (addedSet.has(p.id)) arrive(p, (k - 1) * 60, k, arrivalTiming(k, from));
      else motion.to(p.id, { angle: target[k] }, { duration: FAN_COMPRESS_MS, ease: EASE_SETTLE });
    });
    return { busy, done: 0 };
  }

  // С семи: остальные сходятся к новому шагу.
  next.forEach((p, k) => {
    if (!addedSet.has(p.id))
      motion.to(p.id, { angle: target[k] }, { duration: FAN_COMPRESS_MS, ease: EASE_SETTLE });
  });
  if (added.length === 1) {
    // Одна: выходит из-под следующей (последняя — из-под пластины 0 у шва) на шаг против часовой.
    const k = ids.indexOf(added[0]);
    arrive(next[k], base[k] + step, k, {
      delay: FAN_ARRIVE_DELAY_MS,
      duration: FAN_ARRIVE_MS,
      fade: Math.round(FAN_ARRIVE_MS * 0.7),
      ease: EASE_SETTLE,
    });
    return { busy, done: 0 };
  }
  // Пачка: новые стартуют под последней прежней перед ними — там, где она сейчас, а не где встанет, — и
  // расходятся от неё по часовой тем же движением, каким сходятся прежние (900 мс, EASE_SETTLE): все углы
  // идут по одной кривой, и ни в одном кадре пластина не заходит на чужое место. Проступают, когда пачка
  // уже отошла от неё, — без серой пелены поверх светлых.
  next.forEach((p, k) => {
    if (!addedSet.has(p.id)) return;
    let j = k - 1;
    while (j >= 0 && addedSet.has(ids[j])) j--;
    const anchor = j >= 0 ? (motion.value(ids[j])?.angle ?? target[j]) : 0;
    const t = {
      delay: 0,
      duration: FAN_COMPRESS_MS,
      fade: Math.round(FAN_COMPRESS_MS * 0.7),
      ease: EASE_SETTLE,
    };
    arrive(p, anchor, k, t, FAN_ARRIVE_DELAY_MS);
  });
  return { busy, done: 0 };
}

/* ---------- Компонент ---------- */

export function ServiceFan({
  plates,
  label,
  activeId = null,
  pinnedId = null,
  onActive,
  onPin,
  onEnter,
  interactive = true,
  bloom = 'session',
  arriving,
  onSeen,
  count = true,
  debugSectors = false,
  controls,
  className,
}: ServiceFanProps) {
  const uid = safeId(useId());
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const motion = useFanMotion(svgRef);

  // Уходящие пластины остаются на экране, пока складываются.
  const key = plates.map((p) => p.id).join('|');
  const [track, setTrack] = useState<{ key: string; plates: readonly FanPlate[]; leaving: Leaving[] }>(
    () => ({
      key,
      plates,
      leaving: [],
    }),
  );
  if (track.key !== key) {
    const nextIds = new Set(plates.map((p) => p.id));
    const prevIds = new Set(track.plates.map((p) => p.id));
    const removed = track.plates.filter((p) => !nextIds.has(p.id));
    const added = plates.filter((p) => !prevIds.has(p.id));
    const animate =
      removed.length > 0 && added.length === 0 && removed.length <= FAN_BATCH_MAX && !prefersReducedMotion();
    const still = track.leaving.filter((l) => !nextIds.has(l.plate.id));
    const order = track.plates.map((p) => p.id);
    const gone = animate
      ? removed.map((plate) => ({ plate, before: order.slice(0, order.indexOf(plate.id)).reverse() }))
      : [];
    setTrack({ key, plates, leaving: [...still, ...gone] });
  }
  const shown = mergeShown(plates, track.leaving);

  // Сбой прошёл — пластина остывает к светлой за duration-slow, а не белеет скачком (R34): до конца
  // перехода цвета (transitionend) её переход медленный, потом снова обычный — для притухания.
  const alertKey = shown
    .filter(({ plate: p }) => p.on && p.alert)
    .map(({ plate: p }) => p.id)
    .join('|');
  const [cool, setCool] = useState<{ key: string; ids: readonly string[] }>({ key: alertKey, ids: [] });
  if (cool.key !== alertKey) {
    const now = new Set(alertKey.split('|'));
    const here = new Set(shown.map(({ plate: p }) => p.id));
    const ids = [...new Set([...cool.ids, ...cool.key.split('|')])].filter(
      (id) => here.has(id) && !now.has(id),
    );
    setCool({ key: alertKey, ids });
  }
  const cooling = new Set(cool.ids);
  const onTransitionEnd = (e: TransitionEvent<SVGSVGElement>) => {
    if (e.propertyName !== 'fill') return;
    const g = (e.target as Element).closest<SVGGElement>('[data-plate],[data-seam-copy]');
    const id = g?.dataset.plate ?? g?.dataset.seamCopy;
    if (id && cooling.has(id)) setCool((c) => ({ ...c, ids: c.ids.filter((x) => x !== id) }));
  };

  const n = plates.length;
  const total = shown.length;
  // С семи на экране — черепица со швом; порядок слоёв меняется, только пока пластины не касаются:
  // уходящие держат прежний порядок до конца ухода.
  const tiled = total >= 7;
  const activeIdx = activeId ? plates.findIndex((p) => p.id === activeId) : -1;
  const active = activeIdx >= 0 ? plates[activeIdx] : null;
  const target = fanTargets(n, activeIdx);
  const g = tiled ? seamWidth(total) : 0;
  const m = tiled ? seamCount(total) : 0;

  // Сторона веера в px: размер числа в кольце и запас обрезки у шва.
  const [px, setPx] = useState(0);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    // Первый замер приходит сам — после раскладки, до отрисовки кадра; применяется сразу.
    const ro = new ResizeObserver(() => flushSync(() => setPx(el.getBoundingClientRect().width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const unitsPerPx = (FAN_HALF * 2) / (px || FAN_HALF);
  const seamClip = petalContour(g + SEAM_CLIP_PX * unitsPerPx);

  /* ---------- Движение ---------- */

  const [blooms] = useState(() => bloom === 'always' || (bloom === 'session' && !readBloomed()));
  const prev = useRef<{ ids: string[]; active: string | null; on: Map<string, boolean> } | null>(null);
  const busyUntil = useRef(0);
  const timers = useRef(new Set<number>());
  const later = (ms: number, fn: () => void) => {
    const t = window.setTimeout(() => {
      timers.current.delete(t);
      fn();
    }, ms);
    timers.current.add(t);
  };

  // Последние пропсы — для отложенного прихода.
  const latestRef = useRef({ plates, activeIdx });
  useLayoutEffect(() => {
    latestRef.current = { plates, activeIdx };
  });

  /** Движение сейчас не играет (R19, скрытая вкладка браузера): всё сразу в цели, наведение не ждёт. */
  const instant = () => prefersReducedMotion() || document.hidden;
  /** Веер на экране: фоновая вкладка рабочей области остаётся смонтированной, но скрыта (hidden). */
  const onScreen = () => (wrapRef.current?.getClientRects().length ?? 0) > 0;
  /** Пришедшие, пока веера не было на экране, и несыгранное раскрытие: ждут показа. */
  const held = useRef<{ fresh: string[]; bloom: boolean } | null>(null);
  const remember = () => {
    prev.current = {
      ids: plates.map((p) => p.id),
      active: activeId,
      on: new Map(plates.map((p) => [p.id, p.on])),
    };
  };

  // Показ: раскрытие, приход того, что подключили без страницы, или сразу на месте.
  const present = (arrived: readonly string[], bloomNow: boolean) => {
    const ids = plates.map((p) => p.id);
    const fresh = arrived.filter((id) => ids.includes(id));
    if (bloomNow && !instant() && n > 0) {
      busyUntil.current = performance.now() + bloomFan({ motion, next: plates, activeIdx });
      writeBloomed();
    } else if (!instant() && fresh.length > 0 && fresh.length <= FAN_BATCH_MAX) {
      const old = ids.filter((id) => !fresh.includes(id));
      const oldTarget = fanTargets(old.length);
      for (const p of plates) {
        const k = old.indexOf(p.id);
        if (k >= 0) motion.jump(p.id, { angle: oldTarget[k], opacity: 1, light: lightOf(p) });
        else motion.jump(p.id, { angle: 0, opacity: 0, light: FAN_LIGHT_OFF });
      }
      busyUntil.current = Infinity;
      later(ARRIVE_ON_SHOW_MS, () => {
        const latest = latestRef.current;
        const { busy } = changeFan({ motion, prev: old, next: latest.plates, activeIdx: latest.activeIdx });
        busyUntil.current = instant() ? 0 : performance.now() + busy;
      });
    } else place({ motion, next: plates, activeIdx });
    remember();
    onSeen?.(ids);
  };

  // Первый показ сохраняет исходные параметры; последующие смены ведёт эффект ниже.
  const initialPresentation = useRef({
    onScreen,
    present,
    arriving,
    blooms,
    motion,
    plates,
    activeIdx,
    remember,
  });
  useLayoutEffect(() => {
    const initial = initialPresentation.current;
    if (initial.onScreen()) initial.present(initial.arriving ?? [], initial.blooms);
    else {
      // Смонтирован в скрытой вкладке: пластины сразу на местах, раскрытие и приход — после показа.
      place({ motion: initial.motion, next: initial.plates, activeIdx: initial.activeIdx });
      held.current = { fresh: [...(initial.arriving ?? [])], bloom: initial.blooms };
      initial.remember();
    }
    const pending = timers.current;
    return () => {
      for (const t of pending) window.clearTimeout(t);
      pending.clear();
      prev.current = null;
    };
  }, []);

  // Смены после показа: состав, активная пластина, свет.
  useLayoutEffect(() => {
    const p = prev.current;
    if (!p) return;
    const ids = plates.map((x) => x.id);
    const sameIds = p.ids.length === ids.length && p.ids.every((id, i) => id === ids[i]);
    const visible = onScreen();
    if (!visible && (held.current || !sameIds)) {
      // Не на экране: всё сразу на местах, ушедшие — сразу; пришедшие ждут показа (spec-4 §7.3).
      const h = held.current ?? { fresh: [], bloom: false };
      const fresh = [...h.fresh, ...ids.filter((id) => !p.ids.includes(id))].filter((id) => ids.includes(id));
      held.current = { fresh, bloom: h.bloom };
      place({ motion, next: plates, activeIdx });
      if (track.leaving.length > 0) later(0, () => setTrack((t) => ({ ...t, leaving: [] })));
      remember();
      onSeen?.(ids.filter((id) => !fresh.includes(id)));
      return;
    }
    if (visible && held.current) {
      // Показали (размер веера пришёл заново — отсюда и рендер): ждавшее выходит, как при первом показе.
      const h = held.current;
      held.current = null;
      present(h.fresh, h.bloom);
      return;
    }
    if (!sameIds) {
      const { busy, done } = changeFan({ motion, prev: p.ids, next: plates, activeIdx });
      if (busy > 0 && !instant()) busyUntil.current = performance.now() + busy;
      if (done > 0) {
        const goneIds = new Set(p.ids.filter((id) => !ids.includes(id)));
        later(done, () =>
          setTrack((t) => ({ ...t, leaving: t.leaving.filter((l) => !goneIds.has(l.plate.id)) })),
        );
      }
      // Отрыв при смене состава: активная на своём месте отошла, уходящие складываются у заклёпки.
      const tween = partTween(activeId !== null);
      for (const { plate: x } of shown)
        motion.to(x.id, { lift: liftOf(ids.indexOf(x.id), activeIdx) }, tween);
    } else if (p.active !== activeId) {
      // Раздвигание и отрыв активной: раскрыть быстро, вернуть медленнее; переход между пластинами — с
      // места, волной; прежняя активная возвращается к заклёпке тем же движением, что новая отходит.
      const tween = partTween(activeId !== null);
      plates.forEach((x, k) => motion.to(x.id, { angle: target[k], lift: liftOf(k, activeIdx) }, tween));
    }
    // Свет: включение наливается от заклёпки к кончику, выключение стекает к заклёпке.
    for (const x of plates) {
      const was = p.on.get(x.id);
      if (was === undefined || was === x.on) continue;
      if (x.on) motion.to(x.id, { light: FAN_LIGHT_ON }, { duration: FAN_LIGHT_ON_MS, ease: EASE_SETTLE });
      else motion.to(x.id, { light: FAN_LIGHT_OFF }, { duration: FAN_LIGHT_OFF_MS, ease: EASE_FOLD });
    }
    remember();
    if (!sameIds) onSeen?.(ids);
  });

  /* ---------- Указатель и клавиатура ---------- */

  const lastActive = useRef<string | null>(null);
  useEffect(() => {
    if (activeId) lastActive.current = activeId;
  }, [activeId]);

  const lastHit = useRef<string | null>(null);
  const hitAt = (clientX: number, clientY: number): string | 'ring' | null => {
    const svg = svgRef.current;
    if (!svg || n === 0) return null;
    const r = svg.getBoundingClientRect();
    const s = (FAN_HALF * 2) / Math.min(r.width, r.height);
    const h = hitTest(
      n,
      target,
      (clientX - r.left - r.width / 2) * s,
      (clientY - r.top - r.height / 2) * s,
      activeIdx,
    );
    return typeof h === 'number' ? plates[h].id : h;
  };
  const report = (id: string | null) => {
    if (id === lastHit.current) return;
    lastHit.current = id;
    onActive?.(id, 'pointer');
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    if (e.pointerType === 'touch') return;
    const h = hitAt(e.clientX, e.clientY);
    const id = h === 'ring' ? null : h;
    // Рука — только над попаданием.
    e.currentTarget.style.cursor = id ? 'pointer' : '';
    // Пока идёт раскрытие или приход, наведение не включается.
    if (performance.now() < busyUntil.current) return;
    report(id);
  };
  const onPointerLeave = () => report(null);
  const onClick = (e: MouseEvent<SVGSVGElement>) => {
    const h = hitAt(e.clientX, e.clientY);
    if (h && h !== 'ring') onPin?.(pinnedId === h ? null : h);
    else onPin?.(null);
  };

  const onFocus = (e: FocusEvent<SVGSVGElement>) => {
    // Только фокус с клавиатуры: нажатие мышью ведёт наведение и закрепление.
    if (!e.currentTarget.matches(':focus-visible') || n === 0) return;
    const has = (id: string | null) => id !== null && plates.some((p) => p.id === id);
    const id = has(pinnedId) ? pinnedId : has(lastActive.current) ? lastActive.current : plates[0].id;
    if (id) onActive?.(id, 'keyboard');
  };

  const typed = useRef({ text: '', at: 0 });
  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    if (n === 0 || e.ctrlKey || e.metaKey || e.altKey) return;
    const cur = activeIdx;
    const go = (k: number) => {
      e.preventDefault();
      onActive?.(plates[((k % n) + n) % n].id, 'keyboard');
    };
    const quarter = Math.ceil(n / 4);
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        return go(cur < 0 ? 0 : cur + 1);
      case 'ArrowLeft':
      case 'ArrowUp':
        return go(cur < 0 ? n - 1 : cur - 1);
      case 'Home':
        return go(0);
      case 'End':
        return go(n - 1);
      case 'PageDown':
        return go(Math.max(cur, 0) + quarter);
      case 'PageUp':
        return go(Math.max(cur, 0) - quarter);
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const id = active?.id ?? plates[0].id;
        onPin?.(id);
        onEnter?.(id);
        return;
      }
      case 'Escape':
        if (!active && !pinnedId) return;
        e.preventDefault();
        e.stopPropagation();
        onPin?.(null);
        onActive?.(null, 'keyboard');
        return;
    }
    if (e.key.length !== 1 || e.key === ' ') return;
    // Буква — к сервису по началу имени, с транслитом и раскладкой (R40); совпадения по кругу.
    const now = performance.now();
    const t = typed.current;
    t.text = now - t.at < TYPEAHEAD_MS ? t.text + e.key : e.key;
    t.at = now;
    const find = (query: string, from: number) => {
      const variants = queryVariants(query);
      for (let i = 0; i < n; i++) {
        const j = (((from + i) % n) + n) % n;
        const name = fold(plates[j].label);
        if (variants.some((v) => name.startsWith(v))) return j;
      }
      return -1;
    };
    let k = find(t.text, t.text.length === 1 ? cur + 1 : Math.max(cur, 0));
    // Одна буква подряд («sss») и такого начала нет — шаг по совпадениям с этой буквы, как в списках Radix;
    // «ии» по-прежнему находит «ИИ-…».
    if (k < 0 && /^(.)\1+$/u.test(fold(t.text))) k = find(e.key, cur + 1);
    if (k >= 0) go(k);
  };

  /* ---------- Рисунок ---------- */

  const optionId = (id: string) => `${uid}-opt-${safeId(id)}`;
  const lightId = (id: string) => `${uid}-light-${safeId(id)}`;
  const seamId = `${uid}-seam`;
  const indexOf = new Map(plates.map((p, k) => [p.id, k]));
  const heads = shown.slice(0, m);
  const tails = shown.slice(total - m);
  const order = tiled ? shown : [...shown].reverse();
  const countSize = px > 0 && px < 200 ? 'text-2xs' : px > 0 && px < 360 ? 'text-base' : 'text-xl';

  const sectors =
    debugSectors && n > 0
      ? [
          ...plates.map((p, k) => ({
            key: p.id,
            d: sectorPath(
              target[k],
              k < n - 1 ? target[k + 1] : n >= 6 ? target[0] + 360 : { tail: target[k] },
            ),
          })),
          ...(n < 6 ? [{ key: '__slack', d: sectorPath(target[0] - FAN_PARTIAL_SLACK, target[0]) }] : []),
        ]
      : [];

  return (
    <div ref={wrapRef} data-slot="service-fan" className={cn('relative', className)}>
      <svg
        ref={svgRef}
        viewBox={`${-FAN_HALF} ${-FAN_HALF} ${FAN_HALF * 2} ${FAN_HALF * 2}`}
        className="group block size-full overflow-visible outline-none"
        role={interactive ? 'listbox' : 'img'}
        aria-label={label}
        tabIndex={interactive && n > 0 ? 0 : undefined}
        aria-activedescendant={interactive && active ? optionId(active.id) : undefined}
        aria-controls={interactive ? controls : undefined}
        onPointerMove={interactive ? onPointerMove : undefined}
        onPointerLeave={interactive ? onPointerLeave : undefined}
        onClick={interactive ? onClick : undefined}
        onFocus={interactive ? onFocus : undefined}
        onKeyDown={interactive ? onKeyDown : undefined}
        onTransitionEnd={cooling.size > 0 ? onTransitionEnd : undefined}
      >
        <defs>
          {/* Свет каждой пластины — круг от заклёпки; радиус пишет движок. */}
          {shown.map(({ plate: p }) => (
            <clipPath key={p.id} id={lightId(p.id)}>
              <circle data-light={p.id} />
            </clipPath>
          ))}
          {/* Шов: контуры хвостовых пластин в их текущих углах, чуть шире самих пластин. Векторная маска,
              а не clipPath: обрезка режет каждый слой копии отдельно, и на её кромке внутри светлой
              пластины проступает ночная основа волоском; маска режет уже собранную копию. */}
          {m > 0 && (
            <mask
              id={seamId}
              className="mask-type-alpha"
              maskUnits="userSpaceOnUse"
              x={-FAN_HALF}
              y={-FAN_HALF}
              width={FAN_HALF * 2}
              height={FAN_HALF * 2}
            >
              {tails.map(({ plate: p }) => (
                <path key={p.id} d={seamClip} data-seam-clip={p.id} />
              ))}
            </mask>
          )}
        </defs>

        {/* До шести следующая под предыдущей (как в знаке), с семи — поверх. */}
        <g>
          {order.map(({ plate: p, leaving }) => {
            const k = indexOf.get(p.id) ?? -1;
            const live = !leaving && k >= 0;
            const option = interactive && live;
            return (
              <Plate
                key={p.id}
                id={p.id}
                on={p.on}
                alert={p.on && !!p.alert}
                cooling={cooling.has(p.id)}
                active={live && p.id === active?.id}
                pinned={live && p.id === pinnedId}
                leaving={leaving}
                optionId={option ? optionId(p.id) : undefined}
                posinset={option ? k + 1 : undefined}
                setsize={option ? n : undefined}
                label={option ? optionLabel(p, k, n) : undefined}
                g={g}
                dim={active !== null && p.id !== active.id}
                light={lightId(p.id)}
              />
            );
          })}
        </g>

        {/* Копии головных пластин поверх хвостовых — только внутри их контуров. */}
        {m > 0 && (
          <g mask={`url(#${seamId})`} aria-hidden>
            {heads.map(({ plate: p }) => (
              <g key={p.id} data-seam-copy={p.id}>
                <PlateLayers
                  g={g}
                  dim={active !== null && p.id !== active.id}
                  light={lightId(p.id)}
                  alert={p.on && !!p.alert}
                  cooling={cooling.has(p.id)}
                />
              </g>
            ))}
          </g>
        )}

        {sectors.length > 0 && (
          <g aria-hidden className="pointer-events-none">
            {sectors.map((s, i) => (
              <path
                key={s.key}
                d={s.d}
                className={cn(i % 2 ? 'fill-chart-2' : 'fill-chart-4', 'stroke-night')}
                fillOpacity={0.35}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>
        )}

        {/* Фокус с клавиатуры: полный силуэт активной пластины поверх стопки (R15). */}
        {interactive && active && (
          <path
            data-focus={active.id}
            d={PETAL_D}
            fill="none"
            className="stroke-ring opacity-0 group-focus-visible:opacity-100"
            strokeWidth={2}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            aria-hidden
          />
        )}

        {/* Кольцо заклёпки — как у знака с ушедшим ядром; живое ядро одно, оно в меню (R41). */}
        <circle
          r={FAN_RING_R}
          fill="none"
          className="stroke-petal"
          strokeWidth={1.25}
          strokeOpacity={0.85}
          vectorEffect="non-scaling-stroke"
        />
        {/* Фокус с клавиатуры в покое (после Esc, «К сводке»): кольцо заклёпки цветом фокуса — место не теряется. */}
        {interactive && !active && n > 0 && (
          <circle
            r={FAN_RING_R}
            fill="none"
            className="stroke-ring opacity-0 group-focus-visible:opacity-100"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            aria-hidden
          />
        )}
      </svg>
      {count && n > 0 && (
        <div aria-hidden className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className={cn('font-semibold text-petal tabular-nums', countSize)}>{n}</span>
        </div>
      )}
    </div>
  );
}

interface LayersProps {
  g: number;
  dim: boolean;
  light: string;
  /** Сбой: свет пластины красный. */
  alert: boolean;
  /** Сбой только что прошёл: красный остывает к светлому за duration-slow. */
  cooling: boolean;
}

/** Свет пластины: светлый или красный при сбое; притухший — та же смесь с ночью. */
function lightFill(alert: boolean, dim: boolean): string {
  if (alert) return dim ? 'fill-petal-alert-dim' : 'fill-petal-alert';
  return dim ? 'fill-petal-dim' : 'fill-petal';
}

/*
  Слои пластины: ночной шов (с семи), основа — ночь с контуром, свет — та же пластина под кругом света.
  Притухшая — цветом (petal-dim, у сбоя — притухший красный), не прозрачностью. memo: при смене активной
  перерисовываются две пластины, а не весь веер.
*/
const PlateLayers = memo(function PlateLayers({ g, dim, light, alert, cooling }: LayersProps) {
  return (
    <>
      {g > 0 && (
        <path d={PETAL_D} fill="none" className="stroke-night" strokeWidth={2 * g} strokeLinejoin="round" />
      )}
      <path
        d={PETAL_D}
        className={cn(
          'fill-night transition-colors duration-normal',
          dim ? 'stroke-petal-dim' : 'stroke-petal',
        )}
        strokeWidth={1.5}
        strokeOpacity={0.9}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={PETAL_D}
        clipPath={`url(#${light})`}
        className={cn(
          'transition-colors',
          cooling ? 'duration-slow' : 'duration-normal',
          lightFill(alert, dim),
        )}
      />
    </>
  );
});

interface PlateProps {
  id: string;
  on: boolean;
  alert: boolean;
  cooling: boolean;
  active: boolean;
  pinned: boolean;
  leaving: boolean;
  /** Есть у пластины-варианта списка (интерактивный веер, не уходящая). */
  optionId?: string;
  posinset?: number;
  setsize?: number;
  label?: string;
  g: number;
  dim: boolean;
  light: string;
}

/** Пластина: поворот, отрыв и прозрачность пишет движок, React — только вид и голос. */
const Plate = memo(function Plate({
  id,
  on,
  alert,
  cooling,
  active,
  pinned,
  leaving,
  optionId,
  ...p
}: PlateProps) {
  const option = optionId !== undefined;
  return (
    <g
      data-plate={id}
      data-state={on ? 'on' : 'off'}
      data-active={active ? '' : undefined}
      data-pinned={pinned ? '' : undefined}
      data-alert={alert ? '' : undefined}
      data-leaving={leaving ? '' : undefined}
      role={option ? 'option' : undefined}
      id={optionId}
      aria-selected={option ? pinned : undefined}
      aria-posinset={p.posinset}
      aria-setsize={p.setsize}
      aria-label={p.label}
      aria-hidden={option ? undefined : true}
    >
      <PlateLayers g={p.g} dim={p.dim} light={p.light} alert={alert} cooling={cooling} />
    </g>
  );
});

/** Пластина-образец для легенды: светлая — включён, контур — выключен, красная — сбой. */
export function PetalSwatch({ on, alert, className }: { on: boolean; alert?: boolean; className?: string }) {
  return (
    <svg viewBox="-250 20 350 475" aria-hidden className={cn('block size-4 shrink-0', className)}>
      <path
        d={PETAL_D}
        className={cn('stroke-petal', !on ? 'fill-night' : alert ? 'fill-petal-alert' : 'fill-petal')}
        strokeWidth={1.5}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
